import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import { ArrowLeft, Bed, Bathtub, Resize, MapPin, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetailPage() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="pt-32 text-center text-[#4A5568]">Loading…</div>;
  }
  if (!property) {
    return (
      <div className="pt-32 text-center text-[#4A5568]">
        Not found. <Link to="/properties" className="underline">Back</Link>
      </div>
    );
  }

  const title = lang === "en" ? property.title_en : property.title_it;
  const description = lang === "en" ? property.description_en : property.description_it;

  return (
    <div className="pt-24 pb-20 bg-white">
      {/* Hero image */}
      <div className="relative h-[60vh] min-h-[450px] overflow-hidden">
        <img src={property.image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1428]/80 via-[#0A1428]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-[1400px] mx-auto px-6 lg:px-12 pb-12">
          <Link
            to="/properties"
            data-testid="property-back-link"
            className="inline-flex items-center gap-2 text-white/80 hover:text-[#C5A059] text-xs uppercase tracking-widest mb-6"
          >
            <ArrowLeft size={14} /> {t.properties.details.back}
          </Link>
          <p className="text-[#C5A059] uppercase tracking-[0.2em] text-xs font-semibold mb-3">
            {t.properties.filters[property.type] || property.type}
          </p>
          <h1
            data-testid="property-detail-title"
            className="font-serif text-4xl lg:text-6xl text-white leading-[1.05] tracking-tight font-medium max-w-4xl"
          >
            {title}
          </h1>
          <p className="mt-4 text-white/80 flex items-center gap-2">
            <MapPin size={16} className="text-[#C5A059]" />
            {property.location}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pb-8 border-b border-[#0A1428]/10">
            {property.bedrooms > 0 && (
              <div>
                <Bed size={22} weight="duotone" className="text-[#C5A059] mb-2" />
                <div className="text-2xl font-serif font-medium text-[#0A1428]">{property.bedrooms}</div>
                <p className="text-xs uppercase tracking-widest text-[#4A5568] mt-1">{t.properties.details.bedrooms}</p>
              </div>
            )}
            {property.bathrooms > 0 && (
              <div>
                <Bathtub size={22} weight="duotone" className="text-[#C5A059] mb-2" />
                <div className="text-2xl font-serif font-medium text-[#0A1428]">{property.bathrooms}</div>
                <p className="text-xs uppercase tracking-widest text-[#4A5568] mt-1">{t.properties.details.bathrooms}</p>
              </div>
            )}
            <div>
              <Resize size={22} weight="duotone" className="text-[#C5A059] mb-2" />
              <div className="text-2xl font-serif font-medium text-[#0A1428]">{property.area_sqm} m²</div>
              <p className="text-xs uppercase tracking-widest text-[#4A5568] mt-1">{t.properties.details.area}</p>
            </div>
            <div>
              <MapPin size={22} weight="duotone" className="text-[#C5A059] mb-2" />
              <div className="text-base font-medium text-[#0A1428]">{property.location.split(",")[0]}</div>
              <p className="text-xs uppercase tracking-widest text-[#4A5568] mt-1">{t.properties.details.location}</p>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="font-serif text-3xl text-[#0A1428] mb-4 font-medium">{t.properties.details.description}</h2>
            <p className="text-[#4A5568] text-lg leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="bg-[#F8F9FA] p-8 sticky top-28">
            <p className="overline mb-3">{t.properties.details.price}</p>
            <div className="font-serif text-4xl text-[#0A1428] font-semibold mb-6">{property.price}</div>
            <Link
              to="/#contact"
              data-testid="property-contact-cta"
              className="btn-primary w-full justify-center"
            >
              {t.properties.details.contact}
              <ArrowRight size={16} weight="bold" />
            </Link>
            <div className="mt-6 pt-6 border-t border-[#0A1428]/10 text-sm text-[#4A5568]">
              <p className="mb-2"><strong className="text-[#0A1428]">+39 393 590 2294</strong></p>
              <p>makelaars360@gmail.com</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
